"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Calendar, CheckCircle } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { HomeCtaSection } from "@/features/home/presentation/components/home-cta-section";
import type { BlogPost } from "../../types/blog";
import { getBlogCategories } from "../../constants/blogs-data";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import type { SiteFooterContent, SiteNavbarContent } from "@/shared/types/site-shell-content";
import type { HomeCtaSectionContent } from "@/features/home/types/home-page-content";
interface BlogsListingPageProps {
  blogs: BlogPost[];
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  ctaSection: HomeCtaSectionContent;
  locale: AppLocale;
  initialCategory?: string;
}
export function BlogsListingPage(t0) {
  const $ = _c(57);
  const {
    blogs,
    navigation,
    footer,
    ctaSection,
    locale,
    initialCategory
  } = t0;
  const categories = getBlogCategories(locale);
  const matchedCategory = initialCategory ? categories.find(c => c.id.toLowerCase() === initialCategory.toLowerCase()) : null;
  const [selectedCategory, setSelectedCategory] = useState(matchedCategory ? matchedCategory.name : locale === "hi" ? "\u0938\u092D\u0940" : "All");
  let t1;
  if ($[0] !== selectedCategory) {
    t1 = blog => {
      if (selectedCategory === "All" || selectedCategory === "\u0938\u092D\u0940") {
        return true;
      }
      return blog.category === selectedCategory;
    };
    $[0] = selectedCategory;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filteredBlogs = blogs.filter(t1);
  const editorPickMain = blogs[0];
  let containerVariants;
  let itemVariants;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== blogs || $[3] !== editorPickMain || $[4] !== locale || $[5] !== navigation) {
    const editorPickSub = blogs.slice(1, 4);
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        opacity: 0
      };
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        hidden: t7,
        show: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.1
          }
        }
      };
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    containerVariants = t8;
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        opacity: 0,
        y: 30
      };
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = [0.25, 0.1, 0.25, 1];
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        hidden: t9,
        show: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.8,
            ease: t10 as const
          }
        }
      };
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    itemVariants = t11;
    t4 = "relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground selection:bg-accent/20 selection:text-foreground";
    if ($[18] !== navigation) {
      t5 = <SiteNavbar content={navigation} />;
      $[18] = navigation;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    if ($[20] !== editorPickMain || $[21] !== locale) {
      t6 = editorPickMain && <section className="relative h-[100svh] w-full overflow-hidden"><Image src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2400&q=85" alt={editorPickMain.title} fill={true} priority={true} sizes="100vw" className="object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" /><motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.1
        }} className="absolute inset-0 flex flex-col justify-end px-6 pt-24 pb-10 sm:px-10 sm:pb-12 lg:px-16 lg:pb-14"><div className="mx-auto flex w-full max-w-7xl flex-col justify-between gap-6 sm:flex-row sm:items-end"><div className="max-w-2xl"><span className="mb-4 inline-block rounded-full bg-white/20 px-3 py-1 text-[10px] font-semibold tracking-[0.15em] text-white uppercase backdrop-blur-sm">{editorPickMain.category}</span><Link href={localizeHref(locale, `/blogs/${editorPickMain.slug}`)}><h1 className="mb-3 font-heading text-2xl leading-tight font-light tracking-tight text-white drop-shadow-md transition-opacity hover:opacity-90 sm:text-3xl lg:text-4xl xl:text-5xl">{editorPickMain.title}</h1></Link><p className="mb-5 line-clamp-2 max-w-xl text-sm leading-relaxed font-light text-white/75 drop-shadow-sm sm:text-base">{editorPickMain.excerpt}</p><Link href={localizeHref(locale, `/blogs/${editorPickMain.slug}`)} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-xs font-semibold text-foreground shadow-md transition-all duration-300 hover:bg-white/90 hover:shadow-lg">{locale === "hi" ? "\u0905\u0927\u093F\u0915 \u092A\u0922\u093C\u0947\u0902" : "Read more"}<ArrowRight className="h-3.5 w-3.5" /></Link></div><div className="flex shrink-0 flex-col items-start gap-2.5 sm:items-end"><div className="flex items-center gap-2"><div className="relative h-7 w-7 overflow-hidden rounded-full border border-white/40"><Image src={editorPickMain.author.avatarUrl} alt={editorPickMain.author.name} fill={true} className="object-cover" /></div><span className="text-sm font-medium text-white drop-shadow-sm">{editorPickMain.author.name}</span></div><div className="flex items-center gap-2 text-xs font-light text-white/65"><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{editorPickMain.date}</span><span>•</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{editorPickMain.readTime}</span></div></div></div></motion.div></section>;
      $[20] = editorPickMain;
      $[21] = locale;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t2 = "mx-auto max-w-7xl px-4 py-16";
    t3 = editorPickMain && <div className="mt-2 mb-24"><motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12 flex flex-col gap-2 text-center sm:text-left"><p className="text-[11px] font-bold tracking-[0.2em] text-primary uppercase">{locale === "hi" ? "\u0938\u0902\u092A\u093E\u0926\u0915 \u0915\u0940 \u092A\u0938\u0902\u0926" : "Editor's Choice"}</p><h2 className="font-heading text-2xl font-light tracking-tight text-foreground sm:text-3xl lg:text-4xl">{locale === "hi" ? "\u0939\u092E\u093E\u0930\u0947 \u0938\u092C\u0938\u0947 \u0932\u094B\u0915\u092A\u094D\u0930\u093F\u092F \u0914\u0930 \u091A\u0941\u0928\u093F\u0902\u0926\u093E \u0932\u0947\u0916" : "Featured Stories & Insights"}</h2></motion.div><motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="group mb-12 grid grid-cols-1 items-center gap-8 rounded-2xl border border-border/40 bg-card p-4 shadow-[0_8px_30px_rgba(0,0,0,0.015)] transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.03)] lg:grid-cols-12 lg:gap-12"><Link href={localizeHref(locale, `/blogs/${editorPickMain.slug}`)} className="relative block aspect-[16/10] w-full overflow-hidden rounded-xl border border-border/20 bg-muted sm:aspect-[16/9] lg:col-span-7 lg:aspect-[1.3]"><Image src={editorPickMain.imageUrl} alt={editorPickMain.title} fill={true} sizes="(max-width: 1024px) 100vw, 700px" className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /></Link><div className="flex flex-col justify-center lg:col-span-5"><div className="mb-3 flex flex-wrap items-center gap-2"><span className="text-[10px] font-bold tracking-widest text-primary uppercase">{editorPickMain.category}</span>{editorPickMain.tags && editorPickMain.tags.length > 0 && <><span className="text-[10px] text-muted-foreground">•</span><div className="flex flex-wrap gap-1">{editorPickMain.tags.map(_temp)}</div></>}</div><Link href={localizeHref(locale, `/blogs/${editorPickMain.slug}`)}><h3 className="mb-4 line-clamp-3 font-heading text-xl leading-snug font-medium text-foreground transition-colors hover:text-primary sm:text-2xl lg:text-3xl">{editorPickMain.title}</h3></Link><p className="mb-6 line-clamp-3 text-sm leading-relaxed font-light text-muted-foreground">{editorPickMain.excerpt}</p><div className="mb-8 flex flex-wrap items-center gap-4 border-b border-border/40 pb-6 text-xs font-light text-muted-foreground"><div className="flex items-center gap-2"><div className="relative h-6 w-6 overflow-hidden rounded-full border border-border"><Image src={editorPickMain.author.avatarUrl} alt={editorPickMain.author.name} fill={true} className="object-cover" /></div><span className="font-semibold text-foreground">{editorPickMain.author.name}</span></div><span className="text-muted-foreground">•</span><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-muted-foreground" />{editorPickMain.date}</span><span className="text-muted-foreground">•</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-muted-foreground" />{editorPickMain.readTime}</span></div><Link href={localizeHref(locale, `/blogs/${editorPickMain.slug}`)} className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-full bg-primary px-6 py-3 text-xs font-semibold text-white shadow-sm transition-all duration-300 hover:bg-primary hover:shadow-md">{locale === "hi" ? "\u0905\u0927\u093F\u0915 \u092A\u0922\u093C\u0947\u0902" : "Read more"}<ArrowRight className="h-3.5 w-3.5" /></Link></div></motion.div><div className="grid grid-cols-1 gap-8 md:grid-cols-3">{editorPickSub.map(blog_0 => <motion.div key={blog_0.slug} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="group flex flex-col justify-between transition-all duration-500"><div className="flex flex-col"><Link href={localizeHref(locale, `/blogs/${blog_0.slug}`)} className="relative mb-5 block aspect-[16/10] w-full overflow-hidden rounded-xl border border-border/20 bg-muted"><Image src={blog_0.imageUrl} alt={blog_0.title} fill={true} sizes="(max-width: 768px) 100vw, 400px" className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /></Link><div className="mb-2 flex flex-wrap items-center gap-2"><span className="text-[10px] font-bold tracking-widest text-primary uppercase">{blog_0.category}</span>{blog_0.tags && blog_0.tags.length > 0 && <><span className="text-[10px] text-muted-foreground">•</span><div className="flex flex-wrap gap-1">{blog_0.tags.slice(0, 2).map(_temp2)}</div></>}</div><Link href={localizeHref(locale, `/blogs/${blog_0.slug}`)}><h4 className="mb-3 line-clamp-2 font-heading text-base leading-snug font-medium text-foreground transition-colors hover:text-primary sm:text-lg">{blog_0.title}</h4></Link><div className="mb-5 flex items-center gap-3 text-xs font-light text-muted-foreground"><span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-muted-foreground" />{blog_0.date}</span><span className="text-muted-foreground">•</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-muted-foreground" />{blog_0.readTime}</span></div></div><Link href={localizeHref(locale, `/blogs/${blog_0.slug}`)} className="mt-2 inline-flex w-fit cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-5 py-2 text-xs font-semibold text-foreground shadow-sm transition-all duration-300 hover:border-primary hover:bg-primary hover:text-white hover:shadow-md">{locale === "hi" ? "\u0905\u0927\u093F\u0915 \u092A\u0922\u093C\u0947\u0902" : "Read more"}<ArrowRight className="h-3.5 w-3.5" /></Link></motion.div>)}</div><div className="mt-20 border-b border-border/60" /></div>;
    $[2] = blogs;
    $[3] = editorPickMain;
    $[4] = locale;
    $[5] = navigation;
    $[6] = containerVariants;
    $[7] = itemVariants;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    containerVariants = $[6];
    itemVariants = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 30
    };
    t8 = {
      opacity: 1,
      y: 0
    };
    t9 = {
      once: true,
      margin: "-100px"
    };
    t10 = {
      duration: 0.8
    };
    $[23] = t10;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t10 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  const t11 = locale === "hi" ? "\u0936\u094D\u0930\u0947\u0923\u093F\u092F\u093E\u0901 \u0916\u094B\u091C\u0947\u0902" : "Explore Categories";
  let t12;
  if ($[27] !== t11) {
    t12 = <h2 className="mb-10 font-heading text-2xl font-light tracking-tight text-foreground sm:text-3xl">{t11}</h2>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== locale || $[30] !== selectedCategory) {
    t13 = cat => {
      const isActive = selectedCategory === cat.name;
      return <button key={cat.id} onClick={() => setSelectedCategory(isActive ? locale === "hi" ? "\u0938\u092D\u0940" : "All" : cat.name)} className={`group flex h-full cursor-pointer flex-col justify-between text-center transition-all duration-300 focus:outline-none ${isActive ? "scale-[1.03]" : "hover:-translate-y-1"}`}><div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-border/30 bg-muted"><Image src={cat.imageUrl} alt={cat.name} fill={true} sizes="(max-width: 640px) 120px, 150px" className="object-cover grayscale-[30%] transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" /></div><div className="mt-3 flex w-full flex-col items-center"><span className={`w-full truncate text-xs font-semibold tracking-tight transition-colors duration-300 sm:text-sm ${isActive ? "text-primary" : "text-foreground group-hover:text-primary"}`}>{cat.name}</span></div></button>;
    };
    $[29] = locale;
    $[30] = selectedCategory;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = categories.map(t13);
  let t15;
  if ($[32] !== t14) {
    t15 = <div className="mx-auto grid max-w-7xl grid-cols-2 justify-center gap-4 sm:grid-cols-4 sm:gap-5 md:grid-cols-6 lg:grid-cols-8">{t14}</div>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t12 || $[35] !== t15) {
    t16 = <motion.div initial={t7} whileInView={t8} viewport={t9} transition={t10} className="mb-20 text-center">{t12}{t15}</motion.div>;
    $[34] = t12;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== locale || $[38] !== selectedCategory) {
    t17 = <h3 className="font-heading text-2xl font-light tracking-tight text-foreground sm:text-3xl">{selectedCategory === "All" || selectedCategory === "\u0938\u092D\u0940" ? locale === "hi" ? <>स्पॉटलाइट <span className="font-semibold text-primary">ब्लॉग</span></> : <>Spotlight <span className="font-semibold text-primary">Blogs</span></> : locale === "hi" ? <>श्रेणी: <span className="font-semibold text-primary">{selectedCategory}</span></> : <><span className="font-semibold text-primary">{selectedCategory}</span></>}</h3>;
    $[37] = locale;
    $[38] = selectedCategory;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const T0 = AnimatePresence;
  const t18 = "popLayout";
  const t19 = motion;
  const t20 = "hidden";
  const t21 = "show";
  const t22 = "grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12";
  let t23;
  if ($[40] !== itemVariants || $[41] !== locale) {
    t23 = blog_1 => <motion.div key={blog_1.slug} variants={itemVariants} layout={true} className="group flex flex-col justify-between transition-all duration-500"><div className="flex h-full flex-col"><Link href={localizeHref(locale, `/blogs/${blog_1.slug}`)}><div className="relative mb-4 aspect-[16/10] w-full overflow-hidden rounded-xl border border-border/20 bg-muted"><Image src={blog_1.imageUrl} alt={blog_1.title} fill={true} sizes="(max-width: 768px) 100vw, 360px" className="object-cover grayscale-[20%] transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0" /></div></Link><div className="mb-1 flex flex-wrap items-center gap-2"><span className="text-[11px] font-semibold tracking-wide text-primary uppercase">{blog_1.category}</span>{blog_1.tags && blog_1.tags.length > 0 && <><span className="text-[10px] text-muted-foreground">•</span><div className="flex flex-wrap gap-1">{blog_1.tags.slice(0, 2).map(_temp3)}</div></>}</div><Link href={localizeHref(locale, `/blogs/${blog_1.slug}`)}><h4 className="mb-2 line-clamp-2 font-heading text-lg leading-snug font-medium text-foreground transition-colors duration-300 group-hover:text-primary">{blog_1.title}</h4></Link><p className="mb-4 line-clamp-3 text-xs leading-relaxed font-light text-muted-foreground sm:text-sm">{blog_1.excerpt}</p><div className="mt-auto flex items-center gap-2 border-t border-border/30 pt-3"><div className="relative h-6 w-6 overflow-hidden rounded-full border border-border"><Image src={blog_1.author.avatarUrl} alt={blog_1.author.name} fill={true} className="object-cover" /></div><div className="flex items-center gap-1"><span className="text-[11px] font-medium text-foreground">{blog_1.author.name}</span>{blog_1.author.isVerified && <CheckCircle className="h-3 w-3 shrink-0 fill-primary text-background" />}</div></div></div></motion.div>;
    $[40] = itemVariants;
    $[41] = locale;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const t24 = filteredBlogs.map(t23);
  let t25;
  if ($[43] !== containerVariants || $[44] !== t19.div || $[45] !== t24) {
    t25 = <t19.div variants={containerVariants} initial={t20} animate={t21} className={t22}>{t24}</t19.div>;
    $[43] = containerVariants;
    $[44] = t19.div;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== T0 || $[48] !== t25) {
    t26 = <T0 mode={t18}>{t25}</T0>;
    $[47] = T0;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== filteredBlogs.length || $[51] !== locale) {
    t27 = filteredBlogs.length === 0 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="rounded-2xl border border-dashed border-border py-20 text-center"><BookOpen className="mx-auto mb-4 h-10 w-10 text-muted-foreground" /><p className="font-light text-muted-foreground">{locale === "hi" ? "\u0907\u0938 \u0936\u094D\u0930\u0947\u0923\u0940 \u092E\u0947\u0902 \u0935\u0930\u094D\u0924\u092E\u093E\u0928 \u092E\u0947\u0902 \u0915\u094B\u0908 \u0932\u0947\u0916 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" : "No articles found in this category yet. Check back soon."}</p></motion.div>;
    $[50] = filteredBlogs.length;
    $[51] = locale;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== ctaSection) {
    t28 = <HomeCtaSection content={ctaSection} />;
    $[53] = ctaSection;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== footer) {
    t29 = <SiteFooter content={footer} />;
    $[55] = footer;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  return <div className={t4}>{t5}{t6}<section className={t2}>{t3}{t16}<div><div className="mb-12 flex flex-col justify-between gap-4 border-b border-border/60 pb-6 sm:flex-row sm:items-baseline"><div className="flex flex-col gap-1">{t17}<p className="text-xs font-light text-muted-foreground">{filteredBlogs.length}{" "}{filteredBlogs.length === 1 ? locale === "hi" ? "\u0932\u0947\u0916 \u0909\u092A\u0932\u092C\u094D\u0927 \u0939\u0948" : "article available" : locale === "hi" ? "\u0932\u0947\u0916 \u0909\u092A\u0932\u092C\u094D\u0927 \u0939\u0948\u0902" : "articles available"}</p></div></div>{t26}</div>{t27}</section>{t28}{t29}</div>;
}
function _temp3(tag_1) {
  return <span key={tag_1} className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-medium text-muted-foreground normal-case">{tag_1}</span>;
}
function _temp2(tag_0) {
  return <span key={tag_0} className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-medium text-muted-foreground">{tag_0}</span>;
}
function _temp(tag) {
  return <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-medium text-muted-foreground">{tag}</span>;
}